/**
 * Basic address fields component
 */

import { AddressAutocomplete } from "@components/ui/AddressAutocomplete";
import {
  parseAddressComponents,
  extractAptUnit,
} from "@lib/utils/addressParser";
import type { PlaceResult } from "@/hooks/useGooglePlacesAutocomplete";
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent'
import type {
  AddressFormState,
  AddressFormActions,
} from "@/features/applicant/types/address";
interface AddressBasicFieldsProps {
  state: AddressFormState;
  actions: AddressFormActions;
  states?: Array<{
    state_code?: string;
    code?: string;
    state_name?: string;
    name?: string;
  }>;
  isLoadingStates?: boolean;
  fieldErrors?: {
    streetNumber?: string;
    streetName?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  onFieldErrorChange?: (field: string, error?: string) => void;
  /** Optional callback after a place is selected (e.g. clear geocode warning). Structured address errors clear via onFieldErrorChange. */
  onAddressSelect?: () => void;
  prefix?: string; // For previous address fields
  hideAddressField?: boolean; // Hide the address autocomplete field
  hideCheckbox?: boolean; // Hide the "Other / Outside U.S." checkbox
  lockToUS?: boolean; // Restrict address selection and country display to US only
  isReadOnly?: boolean; // Disable all fields when true
  onViewDetail?: () => void; // Handler for View Detail button
  /** Called when city, state, or zip blurs – parent can run geocoding verification */
  onCityStateZipBlur?: () => void;
}

export function AddressBasicFields({
  state,
  actions,
  states = [],
  isLoadingStates = false,
  fieldErrors = {},
  onFieldErrorChange,
  onAddressSelect,
  prefix = "",
  hideAddressField = false,
  hideCheckbox = false,
  lockToUS = false,
  isReadOnly = false,
  onViewDetail,
  onCityStateZipBlur,
}: AddressBasicFieldsProps) {
  const handleAddressSelect = (place: PlaceResult) => {
    const parsed = parseAddressComponents(place);
    actions.setAddress(parsed.formattedAddress || "");
    actions.setStreetNumber(parsed.streetNumber || "");

    actions.setStreetName(parsed.streetName || "");
    actions.setStreetType("");

    actions.setCity(parsed.city || "");
    actions.setState(parsed.state || "");
    actions.setZipCode(parsed.zipCode || "");
    const countryCode = lockToUS ? "US" : parsed.country || "US";
    actions.setCountry(countryCode);
    actions.setOutsideUS(
      lockToUS ? false : Boolean(countryCode && countryCode.toUpperCase() !== "US"),
    );
    const apt = extractAptUnit(parsed.formattedAddress);
    if (apt) actions.setAptUnit(apt);
    // Places fills structured fields without blur/change handlers — clear prior required errors
    onFieldErrorChange?.("streetNumber", undefined);
    onFieldErrorChange?.("streetName", undefined);
    onFieldErrorChange?.("city", undefined);
    onFieldErrorChange?.("state", undefined);
    onFieldErrorChange?.("zipCode", undefined);
    onAddressSelect?.();
  };

  // When user changes the address field (type or select), clear Apt/Unit so we don't save duplicate unit with new address
  const handleAddressChange = (value: string) => {
    actions.setAddress(value);
    actions.setAptUnit("");
  };

  const handleZipCodeChange = (value: string) => {
    const digitsOnly = value.replace(/\D/g, "").slice(0, 6);
    actions.setZipCode(digitsOnly);
    if (onFieldErrorChange) {
      onFieldErrorChange("zipCode", undefined);
    }
  };

  const handleZipCodeBlur = () => {
    if (onFieldErrorChange) {
      const digitsOnly = state.zipCode.replace(/\D/g, "");
      if (!digitsOnly) {
        onFieldErrorChange("zipCode", APPLICANT_CONTENT.errors.fieldRequired.zipCode);
      } else if (digitsOnly.length !== 5 && digitsOnly.length !== 6) {
        onFieldErrorChange("zipCode", APPLICANT_CONTENT.errors.fieldRequired.zipCodeLength);
      }
    }
    onCityStateZipBlur?.();
  };

  const handleStreetNumberChange = (value: string) => {
    actions.setStreetNumber(value);
    if (onFieldErrorChange) {
      onFieldErrorChange("streetNumber", undefined);
    }
  };

  const handleStreetNumberBlur = () => {
    if (onFieldErrorChange) {
      const trimmedValue = state.streetNumber.trim();
      if (!trimmedValue) {
        onFieldErrorChange(
          "streetNumber",
          APPLICANT_CONTENT.errors.fieldRequired.streetNumber,
        );
      }
    }
  };

  const outsideUSId = prefix
    ? `prev-outside-us-checkbox`
    : "outside-us-checkbox";

  return (
    <div className="space-y-6">
      {!hideAddressField && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
          <div className={onViewDetail ? "flex gap-2 items-end" : ""}>
            <div className="flex-grow">
              <AddressAutocomplete
                value={state.address}
                onChange={handleAddressChange}
                onPlaceSelect={handleAddressSelect}
                placeholder="Enter your address"
                label={prefix ? APPLICANT_CONTENT.application.sections.previousAddress.title : APPLICANT_CONTENT.application.sections.currentAddress.address}
                disabled={isReadOnly}
                countryRestriction={lockToUS ? "us" : undefined}
              />
            </div>
            {onViewDetail && (
              <button
                type="button"
                onClick={onViewDetail}
                className="mb-1 px-4 py-2.5 bg-[#1a73e8] text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap"
              >
                View Detail
              </button>
            )}
          </div>
          {!hideCheckbox && !lockToUS && (
            <div className="flex items-center pb-2">
              <input
                type="checkbox"
                id={outsideUSId}
                checked={state.outsideUS}
                onChange={(e) => {
                  if (!isReadOnly) {
                    actions.setOutsideUS(e.target.checked);
                    if (e.target.checked) {
                      actions.setCountry("");
                    } else {
                      actions.setCountry("US");
                    }
                  }
                }}
                disabled={isReadOnly}
                className="mr-2 w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              />
              <label
                htmlFor={outsideUSId}
                className="text-sm font-medium text-gray-700 cursor-pointer"
              >
                Other / Outside U.S.
              </label>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 [&>div]:min-w-0">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Street No.*
          </label>
          <input
            type="text"
            value={state.streetNumber}
            onChange={(e) => handleStreetNumberChange(e.target.value)}
            onBlur={handleStreetNumberBlur}
            placeholder={APPLICANT_CONTENT.application.sections.currentAddress.streetNo}
            required
            disabled={isReadOnly}
            className={`w-full px-4 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${fieldErrors.streetNumber
              ? "border-red-500 focus:ring-red-500"
              : "border-gray-300"
              }`}
          />
          {fieldErrors.streetNumber && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              {fieldErrors.streetNumber}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Street Name* (ex. Main)
          </label>
          <input
            type="text"
            value={state.streetName}
            onChange={(e) => {
              actions.setStreetName(e.target.value);
              if (onFieldErrorChange) {
                onFieldErrorChange("streetName", undefined);
              }
            }}
            onBlur={() => {
              if (onFieldErrorChange && !state.streetName.trim()) {
                onFieldErrorChange(
                  "streetName",
                  APPLICANT_CONTENT.errors.fieldRequired.streetName,
                );
              }
            }}
            placeholder="Street Name"
            disabled={isReadOnly}
            className={`w-full px-4 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${fieldErrors.streetName
              ? "border-red-500 focus:ring-red-500"
              : "border-gray-300"
              }`}
          />
          {fieldErrors.streetName && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              {fieldErrors.streetName}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Apt/Unit
          </label>
          <div className="flex items-center w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all disabled:bg-gray-100">
            <span className="flex-shrink-0 text-gray-500 font-medium select-none">#</span>
            <input
              type="text"
              value={state.aptUnit?.replace(/^#\s*/, "") ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                actions.setAptUnit(v.startsWith("#") ? v.replace(/^#\s*/, "") : v);
              }}
              placeholder="Unit number"
              disabled={isReadOnly}
              className="flex-1 min-w-0 ml-1 border-0 p-0 bg-transparent focus:outline-none focus:ring-0 disabled:bg-transparent disabled:text-gray-500 disabled:cursor-not-allowed"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            City*
          </label>
          <input
            type="text"
            value={state.city}
            onChange={(e) => {
              actions.setCity(e.target.value);
              if (onFieldErrorChange) {
                onFieldErrorChange("city", undefined);
              }
            }}
            onBlur={() => {
              if (onFieldErrorChange && !state.city.trim()) {
                onFieldErrorChange("city", APPLICANT_CONTENT.errors.fieldRequired.city);
              }
              onCityStateZipBlur?.();
            }}
            placeholder={APPLICANT_CONTENT.application.sections.currentAddress.city}
            disabled={isReadOnly}
            className={`w-full px-4 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${fieldErrors.city
              ? "border-red-500 focus:ring-red-500"
              : "border-gray-300"
              }`}
          />
          {fieldErrors.city && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              {fieldErrors.city}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            State*
          </label>
          {(lockToUS || !state.outsideUS) && states && states.length > 0 ? (
            <select
              value={state.state}
              onChange={(e) => {
                actions.setState(e.target.value);
                if (onFieldErrorChange) {
                  onFieldErrorChange("state", undefined);
                }
              }}
              onBlur={() => {
                if (onFieldErrorChange && !state.state.trim()) {
                  onFieldErrorChange("state", APPLICANT_CONTENT.errors.fieldRequired.state);
                }
                onCityStateZipBlur?.();
              }}
              disabled={isReadOnly || isLoadingStates}
              className={`w-full px-4 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-left disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${fieldErrors.state
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300"
                }`}
            >
              <option value="">
                {isLoadingStates ? "Loading..." : "Select State"}
              </option>
              {states.map((s, index) => {
                const value = s.state_code || s.code || "";
                const label = s.state_name || s.name || value;
                return (
                  <option key={s.state_code || `state-${index}`} value={value}>
                    {label}
                  </option>
                );
              })}
            </select>
          ) : (
            <input
              type="text"
              value={state.state}
              onChange={(e) => {
                actions.setState(e.target.value);
                if (onFieldErrorChange) {
                  onFieldErrorChange("state", undefined);
                }
              }}
              onBlur={() => {
                if (onFieldErrorChange && !state.state.trim()) {
                  onFieldErrorChange("state", APPLICANT_CONTENT.errors.fieldRequired.state);
                }
                onCityStateZipBlur?.();
              }}
              placeholder={!lockToUS && state.outsideUS ? "State/Province" : "State"}
              disabled={isReadOnly}
              className={`w-full px-4 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${fieldErrors.state
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300"
                }`}
            />
          )}
          {fieldErrors.state && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              {fieldErrors.state}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Zipcode*
          </label>
          <input
            type="text"
            value={state.zipCode}
            onChange={(e) => handleZipCodeChange(e.target.value)}
            onBlur={handleZipCodeBlur}
            placeholder={!lockToUS && state.outsideUS ? "Postal Code" : "Zipcode"}
            maxLength={6}
            required
            disabled={isReadOnly}
            className={`w-full px-4 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${fieldErrors.zipCode
              ? "border-red-500 focus:ring-red-500"
              : "border-gray-300"
              }`}
          />
          {fieldErrors.zipCode && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              {fieldErrors.zipCode}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Country*
          </label>
          <input
            type="text"
            value={lockToUS ? "US" : state.country}
            placeholder="Country"
            readOnly={lockToUS}
            disabled={isReadOnly || lockToUS}
            onChange={(e) => {
              if (!lockToUS) {
                actions.setCountry(e.target.value);
              }
            }}
            className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${lockToUS ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-white"}`}
          />
        </div>
      </div>
    </div>
  );
}
